// Web push (system notifications) for new orders.
// iPad/iPhone: push only works on iOS 16.4+ when the admin is opened from a
// Home Screen icon, and the permission prompt must come straight from a tap.

const API_BASE = process.env.REACT_APP_API_BASE;
const publicVapidKey = process.env.REACT_APP_PUBLIC_VAPID_KEY;

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

// Unknown (browser doesn't expose the key) counts as the same, to avoid needless resubscribes.
function sameKey(existing, expected) {
  if (!existing) return true;
  const a = new Uint8Array(existing);
  return a.length === expected.length && a.every((byte, i) => byte === expected[i]);
}

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // iPadOS reports itself as a Mac with touch
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isSupported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

// "needs-install" | "unsupported" | "denied" | "enabled" | "disabled"
export async function getPushStatus() {
  if (!isSupported()) {
    return isIOS() && !isStandalone() ? "needs-install" : "unsupported";
  }
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission !== "granted") return "disabled";

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  const current =
    subscription &&
    publicVapidKey &&
    sameKey(subscription.options?.applicationServerKey, urlBase64ToUint8Array(publicVapidKey));
  return current ? "enabled" : "disabled";
}

async function saveSubscription(subscription) {
  if (!localStorage.getItem("token")) throw new Error("Please log in again and retry.");

  // The server takes the user and role from the login token
  const res = await fetch(`${API_BASE}/api/notifications/subscribe`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ subscription }),
  });
  if (!res.ok) throw new Error("Server could not save the subscription.");
}

async function subscribe() {
  if (!publicVapidKey) throw new Error("Push key is not configured (REACT_APP_PUBLIC_VAPID_KEY).");

  await navigator.serviceWorker.register("/sw.js");
  const registration = await navigator.serviceWorker.ready;
  const serverKey = urlBase64ToUint8Array(publicVapidKey);

  let subscription = await registration.pushManager.getSubscription();
  // A subscription made with an older push key can never receive pushes; replace it.
  if (subscription && !sameKey(subscription.options?.applicationServerKey, serverKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: serverKey,
    });
  }
  await saveSubscription(subscription);
}

// Must be called directly from a click/tap handler (before any other await),
// otherwise Safari ignores the permission request.
export async function enablePush() {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return getPushStatus();
  await subscribe();
  return "enabled";
}

export async function disablePush() {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (subscription) {
    await fetch(`${API_BASE}/api/notifications/unsubscribe`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    }).catch(() => {});
    await subscription.unsubscribe();
  }
  return getPushStatus();
}

// Logout: this device stops getting order alerts. Unsubscribing in the
// browser is enough on its own (the push service drops the address and the
// server removes it on its next send); while the login is still valid the
// server row is removed right away too. Logging in again re-registers the
// device without a prompt (refreshPushSubscription).
// Call it BEFORE the login token is removed.
export async function stopPushForThisDevice() {
  const token = localStorage.getItem("token"); // read now: logout clears it next
  try {
    if (!isSupported()) return;
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    if (token) {
      fetch(`${API_BASE}/api/notifications/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
        keepalive: true,
      }).catch(() => {});
    }
    await subscription.unsubscribe();
  } catch (err) {
    console.error("❌ Push unsubscribe on logout failed:", err);
  }
}

export async function sendTestPush() {
  const res = await fetch(`${API_BASE}/api/notifications/test`, {
    method: "POST",
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      data.message ||
        (res.status === 404
          ? "Test endpoint not found - restart/redeploy the API."
          : `Test notification failed (HTTP ${res.status}).`),
    );
  }
  return data;
}

// After login: re-register this device for the logged-in user if permission
// was already given earlier. Never shows a prompt.
export async function refreshPushSubscription() {
  try {
    // The FinGlider setup account doesn't get order alerts
    if (localStorage.getItem("role") === "fingliderAdmin") return;
    if (!isSupported() || Notification.permission !== "granted") return;
    await subscribe();
  } catch (err) {
    console.error("❌ Push subscription error:", err);
  }
}
